/* Byggesagskonsulenten – theme behaviour (no dependencies) */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Header: scroll state, dropdowns, mobile menu ---------- */
  function initHeader(root) {
    var header = (root || document).querySelector('[data-site-header]');
    if (!header || header.dataset.ready) return;
    header.dataset.ready = 'true';

    var onScroll = function () {
      header.classList.toggle('is-scrolled', window.scrollY > 8);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });

    var dropdownButtons = header.querySelectorAll('[data-dropdown] > button');
    var closeDropdowns = function (except) {
      dropdownButtons.forEach(function (btn) {
        if (btn !== except) btn.setAttribute('aria-expanded', 'false');
      });
    };

    dropdownButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var open = btn.getAttribute('aria-expanded') === 'true';
        closeDropdowns(btn);
        btn.setAttribute('aria-expanded', open ? 'false' : 'true');
      });
      // Hovering one menu item closes any dropdown opened by click on another
      btn.parentElement.addEventListener('mouseenter', function () {
        closeDropdowns(btn);
      });
      btn.parentElement.addEventListener('mouseleave', function (event) {
        if (event.pointerType !== 'touch' && window.matchMedia('(hover: hover)').matches) {
          btn.setAttribute('aria-expanded', 'false');
        }
      });
    });

    document.addEventListener('click', function (event) {
      if (!header.contains(event.target)) closeDropdowns();
    });

    var toggle = header.querySelector('[data-mobile-toggle]');
    var panel = header.querySelector('[data-mobile-nav]');

    var setMobile = function (open) {
      if (!toggle || !panel) return;
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open) {
        // Fill the space below the header, whether or not the top bar is still visible
        panel.style.height = Math.max(0, window.innerHeight - header.getBoundingClientRect().bottom) + 'px';
      }
      panel.hidden = !open;
      header.classList.toggle('is-open', open);
      document.body.classList.toggle('is-locked', open);
      document.dispatchEvent(new CustomEvent('mobile-nav:toggle', { detail: { open: open } }));
      if (open) {
        var first = panel.querySelector('a, summary, button');
        if (first) first.focus({ preventScroll: true });
      }
    };

    if (toggle && panel) {
      toggle.addEventListener('click', function () {
        setMobile(toggle.getAttribute('aria-expanded') !== 'true');
      });
      window.matchMedia('(min-width: 1100px)').addEventListener('change', function (mq) {
        if (mq.matches) setMobile(false);
      });
    }

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      var openBtn = header.querySelector('[data-dropdown] > button[aria-expanded="true"]');
      if (openBtn) {
        closeDropdowns();
        openBtn.focus();
      }
      if (toggle && toggle.getAttribute('aria-expanded') === 'true') {
        setMobile(false);
        toggle.focus();
      }
    });
  }

  /* ---------- Sticky mobile CTA bar ---------- */
  function initMobileBar() {
    var bar = document.querySelector('[data-mobile-cta-bar]');
    if (!bar || bar.dataset.ready) return;
    bar.dataset.ready = 'true';
    var navOpen = false;
    var update = function () {
      bar.classList.toggle('is-visible', window.scrollY > 420 && !navOpen);
    };
    window.addEventListener('scroll', update, { passive: true });
    document.addEventListener('mobile-nav:toggle', function (event) {
      navOpen = event.detail.open;
      update();
    });
    update();
  }

  /* ---------- Scroll reveal ---------- */
  var revealObserver = null;
  function initReveal(root) {
    var items = (root || document).querySelectorAll('.reveal:not(.is-in)');
    if (!items.length) return;
    if (reduceMotion || !('IntersectionObserver' in window)) {
      items.forEach(function (el) {
        el.classList.add('is-in');
      });
      return;
    }
    if (!revealObserver) {
      revealObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-in');
              revealObserver.unobserve(entry.target);
            }
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
      );
    }
    items.forEach(function (el) {
      revealObserver.observe(el);
    });
  }

  /* ---------- Tabs (pricing) ---------- */
  function initTabs(root) {
    (root || document).querySelectorAll('[data-tabs]').forEach(function (wrap) {
      if (wrap.dataset.ready) return;
      wrap.dataset.ready = 'true';
      var tabs = Array.prototype.slice.call(wrap.querySelectorAll('[role="tab"]'));
      var select = function (tab, focus) {
        tabs.forEach(function (t) {
          var selected = t === tab;
          t.setAttribute('aria-selected', selected ? 'true' : 'false');
          t.tabIndex = selected ? 0 : -1;
          var panel = document.getElementById(t.getAttribute('aria-controls'));
          if (panel) panel.hidden = !selected;
        });
        if (focus) tab.focus();
        initReveal(wrap);
      };
      tabs.forEach(function (tab, index) {
        tab.addEventListener('click', function () {
          select(tab, false);
        });
        tab.addEventListener('keydown', function (event) {
          var next = null;
          if (event.key === 'ArrowRight') next = tabs[(index + 1) % tabs.length];
          if (event.key === 'ArrowLeft') next = tabs[(index - 1 + tabs.length) % tabs.length];
          if (next) {
            event.preventDefault();
            select(next, true);
          }
        });
      });
    });
  }

  /* ---------- Shopify Forms: give the app's form the theme's field styles ----------
     The form renders inside a shadow root, so the stylesheet (assets/shopify-forms.css) is
     adopted into it. Without support or if the fetch fails, the form keeps its own look. */
  var formsSheet = null;
  function initShopifyForms(root) {
    if (!('adoptedStyleSheets' in Document.prototype) || !window.fetch) return;
    (root || document).querySelectorAll('[data-forms-css]').forEach(function (card) {
      if (card.dataset.formsReady) return;
      card.dataset.formsReady = 'true';
      if (!formsSheet) {
        formsSheet = fetch(card.dataset.formsCss)
          .then(function (response) {
            return response.ok ? response.text() : Promise.reject(response.status);
          })
          .then(function (css) {
            var sheet = new CSSStyleSheet();
            sheet.replaceSync(css);
            return sheet;
          })
          .catch(function () {
            return null;
          });
      }
      var adopt = function (shadow) {
        formsSheet.then(function (sheet) {
          if (sheet && Array.prototype.indexOf.call(shadow.adoptedStyleSheets, sheet) === -1) {
            shadow.adoptedStyleSheets = Array.from(shadow.adoptedStyleSheets).concat(sheet);
          }
        });
      };
      var scan = function () {
        card.querySelectorAll('shopify-forms-embed').forEach(function (host) {
          if (!host.shadowRoot || host.dataset.themed) return;
          host.dataset.themed = 'true';
          adopt(host.shadowRoot);
          // re-adopt if the app ever resets its style sheets while re-rendering
          new MutationObserver(function () {
            adopt(host.shadowRoot);
          }).observe(host.shadowRoot, { childList: true });
        });
      };
      scan();
      new MutationObserver(scan).observe(card, { childList: true, subtree: true });
    });
  }

  function init(root) {
    initHeader(root);
    initMobileBar();
    initReveal(root);
    initTabs(root);
    initShopifyForms(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  /* Theme editor: re-initialise sections when they are added or changed */
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
